import React, { useEffect, useMemo, useState } from "react";
import api from "../api/client";

/**
 * Edit one object on a network controller — a wireless network, a VLAN, and
 * whatever a vendor declares next.
 *
 * VENDOR-AGNOSTIC BY CONSTRUCTION
 * The form is built from `GET /cloud-controllers/{id}/actions`, which returns
 * the adapter's own declared actions and their editable field names. This
 * component knows nothing about UniFi. Adding an action to WRITE_ACTIONS
 * server-side makes it editable here with no frontend change, which is the
 * same bargain the read tabs already make.
 *
 * TWO THINGS IT MUST GET RIGHT
 *
 * 1. ONLY CHANGED FIELDS ARE SENT. The server merges what it receives into the
 *    live object, so posting every field would push stale values back — and
 *    for a passphrase, posting an untouched empty box would blank the WPA key
 *    of a working network. Untouched fields are omitted entirely.
 *
 * 2. RELAYED CHANGES ARE ASYNCHRONOUS. A controller on the customer's LAN is
 *    unreachable from the appliance, so the change is handed to an on-site
 *    agent and the response is only {mode:"relayed", command_id}. The real
 *    outcome arrives by polling. Treating that 200 as success would report
 *    "saved" for a change that had not been attempted yet, which is the exact
 *    class of lie this product should not tell.
 */

export type ActionMeta = {
  action: string;
  label: string;
  method: string;
  fields: string[];
  /** Which read capability this action edits, e.g. "ssids". */
  target?: string | null;
  /**
   * "run" is an action with nothing to fill in — restarting the gear, for
   * one. It shares this dialog rather than getting its own button somewhere
   * so that it inherits the parts that matter: the writes-enabled gate, the
   * audit entry, and the relayed poll that waits for the real outcome.
   */
  kind?: "update" | "create" | "run";
  /** Write-only fields — the server never reports their current value. */
  secret_fields?: string[];
  /** Suggested values for vendor enums. Guidance, NOT a closed set. */
  options?: Record<string, string[]>;
  /** field -> the capability whose objects it references, e.g. "vlans". */
  refs?: Record<string, string>;
  /** What the operator is agreeing to. Shown instead of a form on a "run". */
  confirm?: string | null;
};

export type ActionsInfo = {
  vendor: string;
  /** The admin's toggle. */
  writes_enabled: boolean;
  /**
   * Whether the STORED CREDENTIAL can carry a change. Separate from the
   * toggle: a UniFi API key reads every tab and cannot write anything,
   * because reads and writes use two different UniFi APIs.
   */
  writes_possible?: boolean;
  writes_blocked_reason?: string;
  /** Non-blocking: writes are allowed but likely to fail, and why. */
  writes_warning?: string;
  relayed: boolean;
  actions: ActionMeta[];
  /**
   * The hub's ONE-CLICK toggles this controller can really carry. A
   * different rail from `actions`: they propose an approval-gated change
   * rather than applying one. Reported so the hub stops rendering a button
   * whose approval fails minutes later on another screen.
   */
  quick_methods?: string[];
};

/**
 * Names that are secret on every controller. This is a floor, not the list:
 * the real list is `action.secret_fields`, declared by the adapter. Keeping a
 * fallback means a vendor added without that declaration still cannot leak a
 * password into a pre-filled box.
 */
const SECRET_FALLBACK = ["x_passphrase", "x_password", "password", "psk", "wpa_psk"];

/**
 * Names known to be boolean/numeric. Only consulted when the current value is
 * absent — on a create there is nothing to infer a type from. When the value
 * IS present its JS type decides, which is exact and needs no vendor list.
 */
const BOOL_BY_NAME = new Set([
  "enabled",
  "is_guest",
  "hide_ssid",
  "vlan_enabled",
  "dhcpd_enabled",
]);
const NUMBER_BY_NAME = new Set(["vlan"]);

/** Human labels. Vendor field names are the API's, not the operator's. */
const FIELD_LABELS: Record<string, string> = {
  name: "Name",
  enabled: "Enabled",
  x_passphrase: "WPA passphrase",
  security: "Security",
  wpa_mode: "WPA mode",
  networkconf_id: "Network",
  is_guest: "Guest network",
  hide_ssid: "Hide SSID",
  vlan_enabled: "Use a VLAN",
  vlan: "VLAN ID",
  ip_subnet: "Subnet (CIDR)",
  dhcpd_enabled: "DHCP server",
  dhcpd_start: "DHCP range start",
  dhcpd_stop: "DHCP range end",
  dhcpd_dns_1: "DNS 1",
  dhcpd_dns_2: "DNS 2",
  domain_name: "Domain name",
  purpose: "Purpose",
  // Gateway / firewall vocabulary. Same rule as above: these are the
  // adapter's generic names, never a vendor's spelling.
  action: "Action",
  source_interfaces: "From interface",
  destination_interfaces: "To interface",
  sources: "Source",
  destinations: "Destination",
  services: "Service",
  schedule: "Schedule",
  nat: "Translate the source address (NAT)",
  logging: "Logging",
  comment: "Comment",
  kind: "Kind",
  value: "Value",
  external_ip: "Outside address",
  external_port: "Outside port",
  internal_ip: "Inside address",
  internal_port: "Inside port",
  port_forward: "Forward the port",
  protocol: "Protocol",
  interface: "Interface",
  destination: "Destination",
  gateway: "Gateway",
  distance: "Distance",
  priority: "Priority",
  alias: "Alias",
  description: "Description",
  address: "Address",
  addressing: "Addressing",
  role: "Role",
  management_access: "Management access",
  vlan_id: "VLAN ID",
  parent: "On interface",
  netmask: "Subnet mask",
  dns: "DNS server",
  lease_seconds: "Lease (seconds)",
  domain: "Domain",
  password: "Password",
  passphrase: "Wireless passphrase",
  email: "Email",
};

const HINTS: Record<string, string> = {
  x_passphrase: "Leave blank to keep the current passphrase.",
  ip_subnet: "e.g. 192.168.20.1/24",
  vlan: "1–4094. Blank or 0 means untagged.",
  password: "Leave blank to keep the current password.",
  passphrase: "Leave blank to keep the current passphrase.",
  // These take more than one value, and the separator is the thing people
  // get wrong. Say it rather than letting the controller reject it.
  sources: "One or more object names, separated by commas.",
  destinations: "One or more object names, separated by commas.",
  services: "One or more service names, separated by commas.",
  source_interfaces: "One or more interface names, separated by commas.",
  destination_interfaces: "One or more interface names, separated by commas.",
  management_access: "Which services answer here, e.g. ping https ssh. Leave empty for none.",
  address: "e.g. 192.168.20.1/24",
  destination: "e.g. 10.20.0.0/16, or 0.0.0.0/0 for a default route.",
  value: "An address or subnet, or a hostname when the kind is a name.",
  lease_seconds: "86400 is one day.",
};

type Props = {
  niId: number;
  action: ActionMeta;
  /** The id the controller knows this object by. */
  objectId: string;
  /** Human name for the dialog title. */
  objectLabel: string;
  /** Current values, keyed by the ADAPTER's field names. */
  initial: Record<string, unknown>;
  /**
   * Objects a reference field can point at, keyed by field name. Supplied by
   * the hub, which fetches the capability the adapter named in `refs`.
   */
  refOptions?: Record<string, { value: string; label: string }[]>;
  relayed: boolean;
  onClose: () => void;
  onDone: () => void;
};

export default function ControllerActionModal({
  niId,
  action,
  objectId,
  objectLabel,
  initial,
  refOptions,
  relayed,
  onClose,
  onDone,
}: Props) {
  const isSecret = useMemo(() => {
    const s = new Set([...(action.secret_fields || []), ...SECRET_FALLBACK]);
    return (f: string) => s.has(f);
  }, [action.secret_fields]);

  /** Boolean when the live value is one; otherwise fall back to the name. */
  const isBool = (f: string) =>
    typeof initial[f] === "boolean" || (!(f in initial) && BOOL_BY_NAME.has(f));
  const isNumber = (f: string) =>
    typeof initial[f] === "number" || (!(f in initial) && NUMBER_BY_NAME.has(f));

  // Secrets are never seeded into the form, so an untouched box cannot
  // overwrite a live passphrase with an empty string.
  const seeded = useMemo(() => {
    const out: Record<string, string> = {};
    for (const f of action.fields) {
      if (isSecret(f)) {
        out[f] = "";
        continue;
      }
      const v = initial[f];
      out[f] = v === undefined || v === null ? "" : String(v);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action.fields, initial, isSecret]);

  const [values, setValues] = useState<Record<string, string>>(seeded);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => setValues(seeded), [seeded]);

  const set = (f: string, v: string) => setValues((cur) => ({ ...cur, [f]: v }));

  const isCreate = action.kind === "create";
  const isRun = action.kind === "run";

  /**
   * What to send.
   *
   * On an UPDATE this is only what the operator actually changed — see note 1
   * in the header; the server merges, so an unchanged field must not be
   * resent.
   *
   * On a CREATE there is nothing to merge into, so every field the operator
   * filled in is sent, and booleans are sent explicitly (true AND false) —
   * omitting an unticked box would leave the controller to guess.
   */
  const changed = useMemo(() => {
    const out: Record<string, unknown> = {};
    for (const f of action.fields) {
      const now = values[f] ?? "";
      const was = seeded[f] ?? "";
      const touched = now !== was;
      if (isSecret(f)) {
        if (now.trim() !== "") out[f] = now; // only ever when typed
        continue;
      }
      if (isBool(f)) {
        if (isCreate || touched) out[f] = now === "true";
        continue;
      }
      if (!isCreate && !touched) continue;
      if (isNumber(f)) {
        if (now.trim() === "") continue;
        const n = Number(now);
        if (!Number.isFinite(n)) continue;
        out[f] = n;
      } else if (now !== "" || touched) {
        out[f] = now;
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, seeded, action.fields, isCreate, isSecret]);

  // On a create, booleans always ride along, so "only booleans" is not a real
  // change — require something typed before the button arms. A "run" has
  // nothing to type and is never blocked by this.
  const nothingToDo = isRun
    ? false
    : isCreate
      ? !(values["name"] || "").trim()
      : Object.keys(changed).length === 0;

  const submit = async () => {
    setBusy(true);
    setErr("");
    setStatus("");
    try {
      const r = await api.post(`/api/v1/cloud-controllers/${niId}/actions`, {
        action: action.action,
        // A create has no object yet; sending an id would be meaningless.
        object_id: isCreate || isRun ? null : objectId,
        fields: isRun ? {} : changed,
      });
      const data = r.data || {};
      if (data.mode !== "relayed") {
        setStatus("Applied.");
        onDone();
        setTimeout(onClose, 700);
        return;
      }
      // Relayed: the 200 only means "queued". Poll for the real outcome.
      setStatus("Sent to the on-site agent — waiting for the controller…");
      const id = data.command_id;
      for (let i = 0; i < 40; i++) {
        await new Promise((res) => setTimeout(res, 3000));
        const p = await api.get(`/api/v1/cloud-controllers/${niId}/actions/${id}`);
        const st = (p.data || {}).status;
        if (st === "completed") {
          setStatus("Applied on the controller.");
          onDone();
          setTimeout(onClose, 900);
          return;
        }
        if (st === "failed" || st === "expired") {
          setErr(
            (p.data || {}).error_message ||
              "The agent could not apply the change on the controller.",
          );
          return;
        }
        setStatus(`Waiting for the on-site agent… (${st || "queued"})`);
      }
      // Deliberately not called success: nothing confirmed it.
      setErr(
        "No result from the on-site agent within two minutes. The change may still " +
          "apply — check the controller before retrying.",
      );
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || "The change was refused.");
    } finally {
      setBusy(false);
    }
  };

  const input = "w-full border rounded px-2 py-1 text-sm dark:bg-gray-900 dark:border-gray-600";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-auto">
        <div className="px-5 py-4 border-b dark:border-gray-700">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100">{action.label}</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{objectLabel}</p>
        </div>

        <div className="px-5 py-4 space-y-3">
          {/* A "run" has no form. What it needs instead is a plain sentence
              saying what happens, because the operator cannot infer the cost
              of it from a label like "Restart". */}
          {isRun && (
            <p className="text-sm rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200 p-3">
              {action.confirm || "This runs immediately on the controller."}
            </p>
          )}
          {action.fields.map((f) => {
            const label = FIELD_LABELS[f] || f;
            const hint = HINTS[f];
            if (isBool(f)) {
              return (
                <label key={f} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={(values[f] ?? "") === "true"}
                    onChange={(e) => set(f, e.target.checked ? "true" : "false")}
                  />
                  <span className="text-gray-700 dark:text-gray-300">{label}</span>
                </label>
              );
            }
            // A reference field is a real dropdown: an id that is not in the
            // list does not exist. If the hub could not load the objects, fall
            // through to a text box rather than offering an empty menu.
            const ref = action.refs?.[f];
            const choices = ref ? refOptions?.[f] : undefined;
            if (ref && choices && choices.length > 0) {
              const known = choices.some((c) => c.value === (values[f] ?? ""));
              return (
                <div key={f}>
                  <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    {label}
                  </label>
                  <select
                    className={input}
                    value={values[f] ?? ""}
                    onChange={(e) => set(f, e.target.value)}
                  >
                    <option value="">—</option>
                    {/* The controller's current value stays selectable even
                        when it is not in the list, so opening the form cannot
                        quietly re-point a network somewhere else. */}
                    {!known && (values[f] ?? "") !== "" && (
                      <option value={values[f]}>{values[f]} (current)</option>
                    )}
                    {choices.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  {hint && <p className="text-[11px] text-gray-400 mt-0.5">{hint}</p>}
                </div>
              );
            }

            const suggestions = action.options?.[f];
            return (
              <div key={f}>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  {label}
                </label>
                <input
                  className={input}
                  type={isSecret(f) ? "password" : isNumber(f) ? "number" : "text"}
                  value={values[f] ?? ""}
                  autoComplete={isSecret(f) ? "new-password" : "off"}
                  // Suggestions, not a closed set — a controller may hold a
                  // value newer than anything this build knows about.
                  list={suggestions ? `opt-${action.action}-${f}` : undefined}
                  onChange={(e) => set(f, e.target.value)}
                />
                {suggestions && (
                  <datalist id={`opt-${action.action}-${f}`}>
                    {suggestions.map((o) => (
                      <option key={o} value={o} />
                    ))}
                  </datalist>
                )}
                {hint && <p className="text-[11px] text-gray-400 mt-0.5">{hint}</p>}
              </div>
            );
          })}

          {relayed && (
            <p className="text-[11px] text-gray-500 dark:text-gray-400 border-t dark:border-gray-700 pt-3">
              This controller is only reachable from the customer's network, so the change is
              delivered by an agent on site. It is not applied until that agent reports back.
            </p>
          )}
          {err && (
            <div className="text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded p-2">
              {err}
            </div>
          )}
          {status && !err && <div className="text-xs text-gray-600 dark:text-gray-300">{status}</div>}
        </div>

        <div className="px-5 py-3 border-t dark:border-gray-700 flex items-center justify-between">
          {/* Saying WHAT will change beats a bare Save: a merge-and-PUT is not
              obviously partial, and an operator should see the blast radius. */}
          <span className="text-[11px] text-gray-500 dark:text-gray-400">
            {isRun
              ? "Runs as soon as you confirm"
              : nothingToDo
                ? isCreate
                  ? "Give it a name to continue"
                  : "No changes yet"
                : isCreate
                  ? `Will create with: ${Object.keys(changed)
                      .map((k) => FIELD_LABELS[k] || k)
                      .join(", ")}`
                  : `Will change: ${Object.keys(changed)
                      .map((k) => FIELD_LABELS[k] || k)
                      .join(", ")}`}
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              disabled={busy}
              className="text-sm px-3 py-1.5 border rounded dark:border-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={busy || nothingToDo}
              className="text-sm px-3 py-1.5 rounded bg-blue-600 text-white disabled:opacity-50"
            >
              {busy
                ? isCreate
                  ? "Creating…"
                  : isRun
                    ? "Running…"
                    : "Applying…"
                : isCreate
                  ? "Create"
                  : isRun
                    ? action.label
                    : "Apply"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
